import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {ListResponse} from '../../interface/response/list-response';
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from 'rxjs/operators';
import {MatSelectChange} from '@angular/material/select';
import {Registration} from '../../interface/registration';
import {RegistrationService} from '../../services/registration.service';
import {DeregistrationPack} from '../../interface/deregistration-pack';
import {REGISTRATION_STATUS_TO_DEREGISTER} from '../../services/dict.service';
import {InsuranceCompany} from '../../interface/insurance-company';
import {Ofwca} from "../../interface/ofwca";

@Component({
  selector: 'app-dict-registrations',
  templateUrl: './dict-registrations.component.html',
  styleUrls: ['./dict-registrations.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictRegistrationsComponent)}
  ]
})
export class DictRegistrationsComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {
  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() contractStatus = false;
  @Input() filterToDeregisterPack?: DeregistrationPack;
  @Input() filterInsuranceCompany?: InsuranceCompany;
  @Input() filterStatus = null;
  @Input() filterOfwca?: Ofwca;
  @Input() filterPartnerId?: string;
  @Input() status = false;
  @Input() rau = false;
  @Input() company = false;
  @Input() regional = false;
  @Input() cession = false;
  @Input() filterInsuranceCompanyId?: number;
  @Output() selectedRegistration: EventEmitter<Registration> = new EventEmitter<Registration>();
  externalControl = true;
  disabled = false;
  items: Registration[] = [];
  value?: string = null;
  registrationFilterCtrl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<Registration>> = new ReplaySubject<ListResponse<Registration>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private registrationService: RegistrationService,
    private fb: FormBuilder,
  ) {
    this.registrationFilterCtrl = fb.control('');
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((registration: Registration) => {
            if (registration.uuid === val) {
              exists = true;
            }
          });

          if (!exists) {
            this.registrationService.loadNotCountedList(0, 3, {
              uuid: val
            }, this.itemsSubject);
          }
        }
      });

    this.registrationFilterCtrl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        if (this.control.value !== null && this.control.value !== '' && this.cession) {
          return;
        }
        if (this.filterToDeregisterPack) {
          this.registrationService.loadNotCountedList(0, 5, {
            context: val,
            company: this.filterToDeregisterPack.company.id,
            insuranceCompany: this.filterToDeregisterPack.insuranceCompany.id,
            isNull$deregistrationPack: 'null',
            agreedToDeregister: 1,
            statusKeys: [REGISTRATION_STATUS_TO_DEREGISTER],
          }, this.itemsSubject);
          return;
        }

        if ((this.filterInsuranceCompany || this.filterInsuranceCompanyId) && this.filterPartnerId && this.filterStatus) {
          this.registrationService.loadNotCountedList(0, 5, {
            context: val,
            insuranceCompany:  this.filterInsuranceCompanyId ?? this.filterInsuranceCompany.id,
            partner: this.filterPartnerId,
            statusKeys: this.filterStatus
          }, this.itemsSubject);
          return;
        }
        if ((this.filterInsuranceCompany || this.filterInsuranceCompanyId) && this.filterOfwca && this.filterStatus) {

          this.registrationService.loadNotCountedList(0, 5, {
            context: val,
            insuranceCompany:  this.filterInsuranceCompanyId ?? this.filterInsuranceCompany.id,
            ofwca: this.filterOfwca.uuid,
            statusKeys: this.filterStatus
          }, this.itemsSubject);
          return;
        }
        if ((this.filterInsuranceCompany || this.filterInsuranceCompanyId) && this.filterStatus) {
          this.registrationService.loadNotCountedList(0, 5, {
            context: val,
            insuranceCompany:  this.filterInsuranceCompanyId ?? this.filterInsuranceCompany.id,
            statusKeys: this.filterStatus,
          }, this.itemsSubject);
          return;
        }
        if ((this.filterInsuranceCompany || this.filterInsuranceCompanyId) && this.filterOfwca) {
          this.registrationService.loadNotCountedList(0, 5, {
            context: val,
            insuranceCompany:  this.filterInsuranceCompanyId ?? this.filterInsuranceCompany.id,
            ofwca: this.filterOfwca.uuid
          }, this.itemsSubject);
          return;
        }

        if ((this.filterInsuranceCompany || this.filterInsuranceCompanyId) && !this.rau) {
          this.registrationService.loadNotCountedList(0, 5, {
            context: val,
            insuranceCompany:  this.filterInsuranceCompanyId ?? this.filterInsuranceCompany.id,
          }, this.itemsSubject);
          return;
        }

        if ((this.filterInsuranceCompany || this.filterInsuranceCompanyId) && this.rau) {
          if (val) {
            this.registrationService.loadNotCountedList(0, 5, {
              context: val,
              insuranceCompany:  this.filterInsuranceCompanyId ?? this.filterInsuranceCompany.id,
            }, this.itemsSubject);
          }
          return;
        }

        if (val){
        this.registrationService.loadNotCountedList(0, 10, {
          ofwcaRegistrationTU: val
        }, this.itemsSubject);
        } else if (this.control.value) {
          this.registrationService.loadNotCountedList(0, 10, {
            uuid: this.control.value
          }, this.itemsSubject);
        }
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<Registration>) => {
        this.items = result.items;
      });
  }


  ngOnChanges(): void {
    if ((this.filterInsuranceCompany || this.filterInsuranceCompanyId) && this.filterOfwca) {
      this.registrationService.loadNotCountedList(0, 5, {
        insuranceCompany: this.filterInsuranceCompanyId ?? this.filterInsuranceCompany.id,
        ofwca: this.filterOfwca.uuid
      }, this.itemsSubject);
      return;
    }
    if ((this.filterInsuranceCompany || this.filterInsuranceCompanyId) && this.filterPartnerId) {
      this.registrationService.loadNotCountedList(0, 5, {
        insuranceCompany: this.filterInsuranceCompanyId ?? this.filterInsuranceCompany.id,
        partner: this.filterPartnerId,
      }, this.itemsSubject);
      return;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.items.forEach((registration: Registration) => {
      if (registration.uuid === e.value) {
        this.selectedRegistration.emit(registration);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }

}
